import { useState } from "react";
import { useBookCta } from "@/hooks/use-book-href";
import { Link } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { FaqSection } from "@/components/seo/faq-section";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { MapPin, Clock, Send } from "lucide-react";
import { useSEO, useSEOSettings, generateLocalBusinessSchema, generateBreadcrumbSchema, generateFAQSchema, generateKnowledgeGraphSchema, LOCATION_KEYWORDS, type SiteInfoSettings } from "@/lib/seo-utils";
import { PageHero } from "@/components/layout/page-hero";
import { SectionHeading } from "@/components/ui/section-heading";
import { LazyVideo } from "@/components/ui/lazy-video";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import { ContactActionLinks } from "@/components/contact/contact-action-links";
import { FormWizard, type WizardStep } from "@/components/ui/form-wizard";

const contactFaqs = [
  {
    question: "How quickly will you respond?",
    answer: "We respond to email and contact form messages within 24 hours, often sooner. For urgent booking questions, call or text us during business hours (9 AM to 6 PM HST).",
  },
  {
    question: "What is your service area?",
    answer: "Our vacation rental is located in Ka'a'awa on Oahu's windward coast (ZIP 96730). We serve guests visiting the windward coast, Kualoa, Kaneohe, and island-wide day trips.",
  },
  {
    question: "Can you help me choose dates?",
    answer: "Yes, share your travel dates and group size and we'll confirm availability. You can also check live availability on our booking page.",
  },
];

const contactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email address"),
  subject: z.string().optional(),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type ContactForm = z.infer<typeof contactSchema>;

export default function ContactPage() {
  const bookCta = useBookCta();
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { seoSettings } = useSEOSettings();
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ['/api/settings/site_info'],
    retry: false,
  });

  const seoHead = useSEO({
    pageType: "contact",
    keywords: `contact vacation rental oahu, ${LOCATION_KEYWORDS.primary}, beachfront rental inquiries, oahu beach house booking questions, vacation rental support, hawaii rental assistance`,
    ogType: "website",
    ogTitle: "Contact Us | Vacation Rental Oahu",
    ogDescription: "Questions about our beachfront vacation rental in Oahu? Contact us directly for personalized assistance with booking and amenities.",
    canonical: "https://vacationrentaloahu.co/contact",
    twitterCard: "summary_large_image",
    structuredData: seoSettings ? [
      generateKnowledgeGraphSchema({ seoSettings, siteInfoSettings: siteSettings }),
      generateLocalBusinessSchema(seoSettings, siteSettings),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Contact", url: "/contact" }
      ]),
      generateFAQSchema(contactFaqs),
    ] : []
  });

  const form = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: ContactForm) => {
      const response = await apiRequest("POST", "/api/contact", data);
      return response.json();
    },
    onSuccess: () => {
      setIsSubmitted(true);
      form.reset();
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Send Message",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ContactForm) => {
    contactMutation.mutate(data);
  };

  const steps: WizardStep[] = [
    {
      title: "About you",
      validate: () => form.trigger(["name", "email"]),
      content: (
        <div className="grid sm:grid-cols-2 gap-8">
          <div className="relative">
            <label htmlFor="name" className="block text-[11px] font-sans font-semibold uppercase tracking-[0.2em] text-driftwood mb-2">Name *</label>
            <input
              id="name"
              className="w-full bg-transparent border-0 border-b border-sand-deep focus:border-ink focus:ring-0 px-0 py-2 text-ink font-sans placeholder:text-driftwood/50 transition-colors"
              placeholder="Jane Doe"
              autoComplete="name"
              {...form.register("name")}
              data-testid="input-name"
            />
            {form.formState.errors.name && (
              <p className="text-[13px] text-red-600 mt-2">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="relative">
            <label htmlFor="email" className="block text-[11px] font-sans font-semibold uppercase tracking-[0.2em] text-driftwood mb-2">Email *</label>
            <input
              id="email"
              type="email"
              className="w-full bg-transparent border-0 border-b border-sand-deep focus:border-ink focus:ring-0 px-0 py-2 text-ink font-sans placeholder:text-driftwood/50 transition-colors"
              placeholder="jane@example.com"
              autoComplete="email"
              inputMode="email"
              {...form.register("email")}
              data-testid="input-email"
            />
            {form.formState.errors.email && (
              <p className="text-[13px] text-red-600 mt-2">{form.formState.errors.email.message}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Your message",
      validate: () => form.trigger(["message"]),
      content: (
        <div className="space-y-8">
          <div className="relative">
            <label htmlFor="subject" className="block text-[11px] font-sans font-semibold uppercase tracking-[0.2em] text-driftwood mb-2">Subject</label>
            <input
              id="subject"
              className="w-full bg-transparent border-0 border-b border-sand-deep focus:border-ink focus:ring-0 px-0 py-2 text-ink font-sans placeholder:text-driftwood/50 transition-colors"
              placeholder="Booking inquiry, property question, etc."
              {...form.register("subject")}
              data-testid="input-subject"
            />
          </div>

          <div className="relative">
            <label htmlFor="message" className="block text-[11px] font-sans font-semibold uppercase tracking-[0.2em] text-driftwood mb-2">Message *</label>
            <textarea
              id="message"
              rows={4}
              className="w-full bg-transparent border-0 border-b border-sand-deep focus:border-ink focus:ring-0 px-0 py-2 text-ink font-sans placeholder:text-driftwood/50 transition-colors resize-none"
              placeholder="Tell us how we can help you..."
              {...form.register("message")}
              data-testid="input-message"
            />
            {form.formState.errors.message && (
              <p className="text-[13px] text-red-600 mt-2">{form.formState.errors.message.message}</p>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
      <Header />

      <main id="main-content">
        <PageHero
          title="Get in Touch"
          subtitle="Have questions about our Beach House Oahu property or need assistance with your booking? We're here to help make your Hawaiian vacation perfect."
          videoSrc={wavesVideo}
          posterSrc={wavesPoster}
          imageAlt="Tranquil Oahu beachfront with crystal clear waters and tropical palms - contact us about your Hawaiian beach house rental"
        />

        <div className="py-16 md:py-24">
          <div className="container mx-auto px-6 max-w-6xl">
            <Breadcrumbs items={[{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }]} className="mb-12" />

            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 lg:items-start">

              {/* Form Column: sticky on desktop while the info column scrolls */}
              <div className="min-w-0 lg:col-span-7 xl:col-span-8 lg:sticky lg:top-28 lg:self-start">
                <div className="bg-sand border border-sand-deep rounded-2xl p-8 md:p-12">
                  <h2 className="text-luxury-2xl font-serif font-normal text-forest mb-8 tracking-luxury-tight">
                    Send us a message
                  </h2>

                  {isSubmitted ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 bg-sand border border-sand-deep rounded-full flex items-center justify-center mx-auto mb-6">
                        <Send className="h-6 w-6 text-ink" />
                      </div>
                      <h3 className="text-luxury-xl font-serif font-normal text-forest mb-4 tracking-luxury-tight">Message Sent</h3>
                      <p className="text-ink-soft mb-8">
                        Thank you for reaching out. We'll respond to your inquiry within 24 hours.
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="inline-flex items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 rounded-lg transition-transform duration-300 hover:-translate-y-0.5"
                        data-testid="send-another-message"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <FormWizard
                      steps={steps}
                      onComplete={form.handleSubmit(onSubmit)}
                      submitLabel="Send message"
                      submittingLabel="Sending..."
                      isSubmitting={contactMutation.isPending}
                    />
                  )}
                </div>
              </div>

              {/* Info Column */}
              <div className="min-w-0 lg:col-span-5 xl:col-span-4 space-y-8">

                <div className="bg-sand border border-sand-deep rounded-2xl p-8">
                  <h3 className="text-luxury-lg font-serif font-normal text-forest mb-6 tracking-luxury-tight">Contact Details</h3>

                  <div className="space-y-6">
                    <div>
                      <p className="text-[11px] font-sans font-semibold uppercase tracking-[0.2em] text-driftwood mb-2">Get in touch</p>
                      <ContactActionLinks
                        phone={siteSettings?.contact_phone}
                        email={siteSettings?.contact_email}
                        variant="links"
                        className="flex-col items-start gap-2"
                        testIdPrefix="contact"
                      />
                    </div>

                    <div>
                      <p className="text-[11px] font-sans font-semibold uppercase tracking-[0.2em] text-driftwood mb-1">Location</p>
                      <p className="text-ink text-luxury-base">Ka'a'awa, HI 96730</p>
                      <p className="text-ink-soft text-sm mt-1">Exact address provided after booking</p>
                    </div>
                  </div>
                </div>

                <div className="bg-sand border border-sand-deep rounded-2xl p-8">
                  <h3 className="text-luxury-lg font-serif font-normal text-forest mb-6 tracking-luxury-tight">Response Times</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-sand-deep pb-4">
                      <span className="text-ink-soft">Email inquiries</span>
                      <span className="font-serif text-ink">&lt; 24 hours</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-sand-deep pb-4">
                      <span className="text-ink-soft">Booking questions</span>
                      <span className="font-serif text-ink">&lt; 4 hours</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-ink-soft">Emergency support</span>
                      <span className="font-serif text-ink">&lt; 1 hour</span>
                    </div>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-2xl group border border-sand-deep bg-shell">
                  <div className="relative h-48">
                    <LazyVideo
                      src={wavesVideo}
                      poster={wavesPoster}
                      autoPlay={false}
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ aspectRatio: '16/9' }}
                      aria-label="Stunning Hawaiian beachfront at dusk"
                    />

                  </div>
                  <div className="relative z-10 text-center p-6 w-full">
                    <h3 className="text-luxury-xl font-serif text-forest mb-2">Ready to book?</h3>
                    <p className="text-ink-soft text-sm mb-6 max-w-[200px] mx-auto opacity-90">
                      Skip the wait and secure your dates today.
                    </p>
                    <Link href={bookCta.href} className="inline-flex w-full sm:w-auto items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans font-semibold uppercase tracking-[0.08em] px-8 py-3 rounded-lg transition-transform duration-300 hover:-translate-y-0.5" data-testid="book-now-cta">
                      {bookCta.choosing ? "Choose a Home" : "Book Now"}
                    </Link>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <section className="py-20 md:py-28 bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-6 max-w-3xl">
            <FaqSection title="Contact FAQ" items={contactFaqs} />
          </div>
        </section>
      </main>

      <StickyMobileBookCta />
      <Footer />
    </div>
    </>
  );
}
